import React, { useCallback, useEffect, useRef, useState } from "react";
import { PORTAL_GUIDE_VIEW_API } from "../apis/apis";

/*
 * View-only PDF viewer for Resources.
 * - The PDF comes from the API as bytes (no storage link), is rendered page by page into
 *   <canvas> with pdf.js (self-hosted in /public/vendor/pdfjs), and has no download / print option.
 * - Right-click, Ctrl/Cmd+S and Ctrl/Cmd+P are blocked while it is open; printing shows nothing.
 * - A light watermark with the candidate's member ID / email is drawn over every page.
 */

const PDFJS_BASE = `${process.env.PUBLIC_URL || ""}/vendor/pdfjs`;
let pdfjsPromise = null;

function loadPdfJs() {
  if (window.pdfjsLib) return Promise.resolve(window.pdfjsLib);
  if (!pdfjsPromise) {
    pdfjsPromise = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = `${PDFJS_BASE}/pdf.min.js`;
      s.async = true;
      s.onload = () => {
        const lib = window.pdfjsLib;
        if (!lib) {
          reject(new Error("PDF viewer failed to load."));
          return;
        }
        lib.GlobalWorkerOptions.workerSrc = `${PDFJS_BASE}/pdf.worker.min.js`;
        resolve(lib);
      };
      s.onerror = () => {
        pdfjsPromise = null;
        reject(new Error("PDF viewer failed to load."));
      };
      document.head.appendChild(s);
    });
  }
  return pdfjsPromise;
}

async function errorMessage(err) {
  const data = err?.response?.data;
  if (data instanceof ArrayBuffer) {
    try {
      const json = JSON.parse(new TextDecoder().decode(data));
      return json?.message || json?.error || null;
    } catch {
      return null;
    }
  }
  return err?.message || null;
}

/* One page: measured up front, rendered when scrolled near the viewport. */
function PdfPage({ pdf, pageNo, width, root }) {
  const holder = useRef(null);
  const canvas = useRef(null);
  const [size, setSize] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let alive = true;
    pdf.getPage(pageNo).then((page) => {
      if (!alive) return;
      const vp = page.getViewport({ scale: 1 });
      setSize({ w: vp.width, h: vp.height });
    });
    return () => {
      alive = false;
    };
  }, [pdf, pageNo]);

  useEffect(() => {
    const el = holder.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(([e]) => setVisible(e.isIntersecting), {
      root: root.current,
      rootMargin: "600px 0px",
    });
    io.observe(el);
    return () => io.disconnect();
  }, [root, size]);

  useEffect(() => {
    if (!visible || !size || !width) return undefined;
    let task = null;
    let alive = true;
    pdf.getPage(pageNo).then((page) => {
      if (!alive || !canvas.current) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const scale = width / size.w;
      const vp = page.getViewport({ scale: scale * dpr });
      const c = canvas.current;
      c.width = Math.floor(vp.width);
      c.height = Math.floor(vp.height);
      task = page.render({ canvasContext: c.getContext("2d"), viewport: vp });
      task.promise.catch(() => {});
    });
    return () => {
      alive = false;
      if (task) task.cancel();
    };
  }, [visible, size, width, pdf, pageNo]);

  const h = size && width ? Math.round((width * size.h) / size.w) : Math.round(width * 1.414);
  return (
    <div ref={holder} className="pdfv-page" style={{ width, height: h }} data-page={pageNo}>
      <canvas ref={canvas} style={{ width, height: h }} aria-label={`Page ${pageNo}`} />
    </div>
  );
}

function PdfViewer({ open, title, examCode, index, watermark, onClose }) {
  const [state, setState] = useState({ loading: true, error: null, pdf: null });
  const [zoom, setZoom] = useState(1);
  const [fitWidth, setFitWidth] = useState(800);
  const [current, setCurrent] = useState(1);
  const scroller = useRef(null);
  const closeBtn = useRef(null);

  // load document
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    let doc = null;
    setState({ loading: true, error: null, pdf: null });
    setZoom(1);
    setCurrent(1);
    (async () => {
      try {
        const [lib, res] = await Promise.all([loadPdfJs(), PORTAL_GUIDE_VIEW_API(examCode, index)]);
        const task = lib.getDocument({ data: new Uint8Array(res.data), isEvalSupported: false });
        doc = await task.promise;
        if (!alive) {
          doc.destroy();
          return;
        }
        setState({ loading: false, error: null, pdf: doc });
      } catch (err) {
        if (!alive) return;
        setState({ loading: false, error: (await errorMessage(err)) || "Unable to open this resource right now.", pdf: null });
      }
    })();
    return () => {
      alive = false;
      if (doc) doc.destroy();
    };
  }, [open, examCode, index]);

  // fit to width
  useEffect(() => {
    if (!open) return undefined;
    const el = scroller.current;
    if (!el) return undefined;
    const measure = () => setFitWidth(Math.max(240, Math.min(el.clientWidth - 32, 1000)));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [open, state.pdf]);

  // block save / print shortcuts, Esc closes, no page scroll behind
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      const k = (e.key || "").toLowerCase();
      if ((e.ctrlKey || e.metaKey) && (k === "s" || k === "p")) {
        e.preventDefault();
        e.stopPropagation();
      }
      if (k === "escape") onClose();
    };
    window.addEventListener("keydown", onKey, true);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.body.classList.add("pdfv-open");
    closeBtn.current?.focus();
    return () => {
      window.removeEventListener("keydown", onKey, true);
      document.body.style.overflow = prev;
      document.body.classList.remove("pdfv-open");
    };
  }, [open, onClose]);

  const onScroll = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    const mid = el.scrollTop + el.clientHeight / 3;
    let n = 1;
    for (const p of el.querySelectorAll(".pdfv-page")) {
      if (p.offsetTop <= mid) n = Number(p.dataset.page);
      else break;
    }
    setCurrent(n);
  }, []);

  if (!open) return null;
  const pages = state.pdf ? state.pdf.numPages : 0;
  const width = Math.round(fitWidth * zoom);
  const marks = Array.from({ length: 12 });

  return (
    <div className="pdfv" role="dialog" aria-modal="true" aria-label={title} onContextMenu={(e) => e.preventDefault()}>
      <div className="pdfv-bar">
        <div className="pdfv-title" title={title}>{title}</div>
        <div className="pdfv-tools">
          {pages > 0 && <span className="pdfv-count">Page {current} / {pages}</span>}
          <button type="button" className="pdfv-btn" onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.25).toFixed(2)))} disabled={!pages || zoom <= 0.5} aria-label="Zoom out">−</button>
          <span className="pdfv-zoom">{Math.round(zoom * 100)}%</span>
          <button type="button" className="pdfv-btn" onClick={() => setZoom((z) => Math.min(3, +(z + 0.25).toFixed(2)))} disabled={!pages || zoom >= 3} aria-label="Zoom in">+</button>
          <button type="button" ref={closeBtn} className="pdfv-btn pdfv-close" onClick={onClose} aria-label="Close">✕</button>
        </div>
      </div>

      <div className="pdfv-body" ref={scroller} onScroll={onScroll}>
        {state.loading && <div className="pdfv-msg">Opening…</div>}
        {state.error && <div className="pdfv-msg pdfv-err">{state.error}</div>}
        {state.pdf && (
          <div className="pdfv-pages" style={{ width }}>
            {Array.from({ length: pages }, (_, i) => (
              <PdfPage key={i + 1} pdf={state.pdf} pageNo={i + 1} width={width} root={scroller} />
            ))}
          </div>
        )}
        {watermark && state.pdf && (
          <div className="pdfv-mark" aria-hidden="true">
            {marks.map((_, i) => (
              <span key={i}>{watermark}</span>
            ))}
          </div>
        )}
      </div>
      <div className="pdfv-foot">View only · downloading and printing are disabled</div>
    </div>
  );
}

export default PdfViewer;
